/**
 * Admin Ads & Marketing - Manage promotional banners (matches Helply reference design)
 */
import { useLanguage } from "@/context/AppContext";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, Plus, Trash2, Check, X, Edit2, Loader2, Upload, Eye, EyeOff, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_FORM = {
  title: '',
  description: '',
  link_url: '',
  image_url: '',
  background_color: '#2563eb',
  text_color: '#ffffff',
  target_audience: 'all',
  priority: 1,
  start_date: '',
  end_date: '',
  cta_text: 'Learn more',
  is_active: true,
};

const TARGET_OPTIONS = [
  { value: 'all', label: 'All Users' },
  { value: 'clients', label: 'Clients Only' },
  { value: 'providers', label: 'Providers Only' },
];

const AdminAdsMarketing = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const logoInputRef = useRef(null);

  useEffect(() => { fetchAds(); }, []);

  const fetchAds = async () => {
    try {
      const res = await axios.get(`${API}/admin/ads`, { withCredentials: true });
      setAds(res.data.ads || []);
    } catch {
      toast.error('Fehler beim Laden der Anzeigen');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = (evt) => {
    const inputEl = evt.currentTarget;
    const file = inputEl.files && inputEl.files.item(0);
    if (!file) return;
    if (file.size > 2097152) { toast.error('Max 2MB'); return; }
    const reader = new FileReader();
    reader.onload = (r) => setForm(p => ({ ...p, image_url: r.target.result }));
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Title and Description are required');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/ads/${editingId}`, form, { withCredentials: true });
        toast.success('Banner updated!');
      } else {
        await axios.post(`${API}/admin/ads`, form, { withCredentials: true });
        toast.success('Banner created!');
      }
      setForm(DEFAULT_FORM);
      setShowForm(false);
      setEditingId(null);
      fetchAds();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error saving banner');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (ad) => {
    setForm({
      title: ad.title || '',
      description: ad.description || '',
      link_url: ad.link_url || '',
      image_url: ad.image_url || '',
      background_color: ad.background_color || '#2563eb',
      text_color: ad.text_color || '#ffffff',
      target_audience: ad.target_audience || 'all',
      priority: ad.priority || 1,
      start_date: ad.start_date || '',
      end_date: ad.end_date || '',
      cta_text: ad.cta_text || 'Learn more',
      is_active: ad.is_active ?? true,
    });
    setEditingId(ad.ad_id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleActive = async (ad) => {
    try {
      await axios.put(`${API}/admin/ads/${ad.ad_id}`, { is_active: !ad.is_active }, { withCredentials: true });
      toast.success(!ad.is_active ? 'Banner activated' : 'Banner deactivated');
      fetchAds();
    } catch { toast.error('Error'); }
  };

  const deleteAd = async (ad_id) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await axios.delete(`${API}/admin/ads/${ad_id}`, { withCredentials: true });
      toast.success('Banner deleted');
      fetchAds();
    } catch { toast.error('Error deleting'); }
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(DEFAULT_FORM);
  };

  return (
    <div className="min-h-screen infirmly-dark bg-gray-100" style={{ background: "radial-gradient(ellipse at top, #0F2557 0%, #0A1A3E 60%, #050B23 100%)" }} data-testid="admin-ads-page">
      {/* Header */}
      <div className="border-b border-white/10 sticky bg-white/[0.04] backdrop-blur top-0 z-40 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-[#0044CC]" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2V5zM2 13a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2z"/>
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{t('adminPageAds')}</h1>
            <p className="text-xs text-gray-500">Manage promotional banners for users</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/admin')} className="rounded-lg gap-2">
            <ChevronLeft className="w-4 h-4" /> Dashboard
          </Button>
          <Button
            onClick={() => { setShowForm(true); setEditingId(null); setForm(DEFAULT_FORM); }}
            className="bg-[#0044CC] hover:bg-[#003399] rounded-lg gap-2"
            data-testid="new-banner-btn"
          >
            <Plus className="w-4 h-4" /> New Banner
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Info bar */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5 text-sm text-blue-800">
          Clicks: Banners are sorted by priority (lower number = higher priority).
        </div>

        {/* Create / Edit Form */}
        {showForm && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm" data-testid="banner-form">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-semibold text-gray-900">{editingId ? 'Edit Banner' : 'Create New Banner'}</h2>
              <button onClick={cancelForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Title + Priority */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Title <span className="text-red-500">*</span></Label>
                  <Input
                    value={form.title}
                    onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                    placeholder="Banner title"
                    className="mt-1"
                    data-testid="banner-title-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Priority (1=highest)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="99"
                    value={form.priority}
                    onChange={e => setForm(p => ({ ...p, priority: parseInt(e.target.value) || 1 }))}
                    className="mt-1"
                    data-testid="banner-priority-input"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <Label className="text-sm font-medium">Description / Message <span className="text-red-500">*</span></Label>
                <Textarea
                  value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Banner message or call-to-action text"
                  className="mt-1 resize-none"
                  rows={3}
                  data-testid="banner-description-input"
                />
              </div>

              {/* Link URL + Logo */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Link URL (optional)</Label>
                  <Input
                    value={form.link_url}
                    onChange={e => setForm(p => ({ ...p, link_url: e.target.value }))}
                    placeholder="https://..."
                    className="mt-1"
                    data-testid="banner-link-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Logo / Image (optional)</Label>
                  <div
                    onClick={() => logoInputRef.current?.click()}
                    className="mt-1 border-2 border-dashed border-gray-300 rounded-lg p-3 flex items-center justify-center gap-2 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors min-h-[42px]"
                    data-testid="logo-upload-area"
                  >
                    {form.image_url ? (
                      <img src={form.image_url} alt="logo" className="h-8 w-8 object-contain rounded" />
                    ) : (
                      <>
                        <Upload className="w-4 h-4 text-gray-400" />
                        <span className="text-xs text-gray-500">Click to upload logo (PNG, JPG, SVG · max 2MB)</span>
                      </>
                    )}
                  </div>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/gif"
                    className="hidden"
                    onChange={handleLogoUpload}
                  />
                </div>
              </div>

              {/* Colors + Target + Status */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm font-medium">Background Color</Label>
                  <div className="mt-1 flex items-center gap-2 border rounded-lg px-3 py-2">
                    <input
                      type="color"
                      value={form.background_color}
                      onChange={e => setForm(p => ({ ...p, background_color: e.target.value }))}
                      className="w-6 h-6 rounded border-0 cursor-pointer flex-shrink-0"
                      data-testid="banner-bg-color"
                    />
                    <span className="text-sm text-gray-600 font-mono">{form.background_color}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Text Color</Label>
                  <div className="mt-1 flex items-center gap-2 border rounded-lg px-3 py-2">
                    <input
                      type="color"
                      value={form.text_color}
                      onChange={e => setForm(p => ({ ...p, text_color: e.target.value }))}
                      className="w-6 h-6 rounded border-0 cursor-pointer flex-shrink-0"
                      data-testid="banner-text-color"
                    />
                    <span className="text-sm text-gray-600 font-mono">{form.text_color}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Target Audience</Label>
                  <select
                    value={form.target_audience}
                    onChange={e => setForm(p => ({ ...p, target_audience: e.target.value }))}
                    className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="banner-target-select"
                  >
                    {TARGET_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Status</Label>
                  <button
                    onClick={() => setForm(p => ({ ...p, is_active: !p.is_active }))}
                    className={`mt-1 w-full flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      form.is_active
                        ? 'bg-green-100 text-green-700 border border-green-300 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                    }`}
                    data-testid="banner-status-toggle"
                  >
                    <div className={`w-2 h-2 rounded-full ${form.is_active ? 'bg-green-500' : 'bg-gray-400'}`} />
                    {form.is_active ? 'Active' : 'Inactive'}
                  </button>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Start Date (optional)</Label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))}
                    className="mt-1"
                    data-testid="banner-start-date"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">End Date (optional)</Label>
                  <Input
                    type="date"
                    value={form.end_date}
                    onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))}
                    className="mt-1"
                    data-testid="banner-end-date"
                  />
                </div>
              </div>

              {/* CTA Text */}
              <div>
                <Label className="text-sm font-medium">CTA Button Text</Label>
                <Input
                  value={form.cta_text}
                  onChange={e => setForm(p => ({ ...p, cta_text: e.target.value }))}
                  placeholder="Learn more"
                  className="mt-1"
                />
              </div>

              {/* Live Preview */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Live Preview</Label>
                <div
                  className="rounded-lg p-4 flex items-start gap-3"
                  style={{ backgroundColor: form.background_color, color: form.text_color }}
                >
                  {form.image_url && (
                    <img src={form.image_url} alt="logo" className="w-10 h-10 object-contain rounded flex-shrink-0 bg-white/20 p-1" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm">{form.title || 'Banner Title'}</p>
                    <p className="text-xs mt-0.5 opacity-90 line-clamp-2">{form.description || 'Banner description'}</p>
                  </div>
                  {form.link_url && (
                    <span
                      className="text-xs font-medium underline flex-shrink-0 opacity-90"
                      style={{ color: form.text_color }}
                    >
                      {form.cta_text}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-2">
                <Button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="bg-[#0044CC] hover:bg-[#003399] rounded-lg gap-2"
                  data-testid="save-banner-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {editingId ? 'Update Banner' : 'Create Banner'}
                </Button>
                <Button variant="outline" onClick={cancelForm} className="rounded-lg">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Banner Count */}
        <p className="text-sm font-semibold text-gray-700">{ads.length} Banner{ads.length !== 1 ? 's' : ''}</p>

        {/* Banner List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
          </div>
        ) : ads.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center text-gray-500">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
              <Plus className="w-6 h-6 text-gray-400" />
            </div>
            <p className="font-medium">No banners yet</p>
            <p className="text-sm mt-1">Click &quot;+ New Banner&quot; to create your first banner</p>
          </div>
        ) : (
          <div className="space-y-3">
            {ads.map(ad => (
              <div
                key={ad.ad_id}
                className="rounded-xl overflow-hidden shadow-sm"
                style={{ backgroundColor: ad.background_color || '#2563eb' }}
                data-testid={`banner-${ad.ad_id}`}
              >
                <div className="flex items-start gap-3 p-4" style={{ color: ad.text_color || '#ffffff' }}>
                  {/* Logo */}
                  {ad.image_url ? (
                    <img src={ad.image_url} alt={ad.title} className="w-12 h-12 object-contain rounded-lg bg-white/20 p-1 flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0 text-lg font-bold">
                      {ad.title?.[0]?.toUpperCase()}
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="font-bold text-sm">{ad.title}</p>
                      <Badge className="text-xs px-1.5 py-0" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'inherit', border: '1px solid rgba(255,255,255,0.3)' }}>
                        P{ad.priority || 1}
                      </Badge>
                    </div>
                    <p className="text-xs opacity-90 line-clamp-2">{ad.description}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <Badge className="text-xs" style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'inherit' }}>
                        {TARGET_OPTIONS.find(t => t.value === ad.target_audience)?.label || 'All Users'}
                      </Badge>
                      <span className="text-xs opacity-70">{ad.impressions || 0} views · {ad.clicks || 0} clicks</span>
                    </div>
                  </div>

                  {/* CTA */}
                  {ad.link_url && (
                    <a
                      href={ad.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-semibold underline flex-shrink-0 self-center flex items-center gap-1 opacity-90 hover:opacity-100"
                      style={{ color: ad.text_color || '#ffffff' }}
                      onClick={e => e.stopPropagation()}
                    >
                      {ad.cta_text || 'Learn more'} <ExternalLink className="w-3 h-3" />
                    </a>
                  )}

                  {/* Admin actions */}
                  <div className="flex items-center gap-1.5 flex-shrink-0 self-center">
                    <button
                      onClick={() => handleEdit(ad)}
                      className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                      title="Edit"
                      data-testid={`edit-banner-${ad.ad_id}`}
                    >
                      <Edit2 className="w-3.5 h-3.5" style={{ color: ad.text_color || '#fff' }} />
                    </button>
                    <button
                      onClick={() => toggleActive(ad)}
                      className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                      title={ad.is_active ? 'Deactivate' : 'Activate'}
                      data-testid={`toggle-banner-${ad.ad_id}`}
                    >
                      {ad.is_active
                        ? <Eye className="w-3.5 h-3.5" style={{ color: ad.text_color || '#fff' }} />
                        : <EyeOff className="w-3.5 h-3.5" style={{ color: ad.text_color || '#fff' }} />
                      }
                    </button>
                    <button
                      onClick={() => deleteAd(ad.ad_id)}
                      className="w-7 h-7 rounded-full bg-white/20 hover:bg-red-400/40 flex items-center justify-center transition-colors"
                      title="Delete"
                      data-testid={`delete-banner-${ad.ad_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" style={{ color: ad.text_color || '#fff' }} />
                    </button>
                    {/* Active/Inactive indicator */}
                    <div className={`ml-1 flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                      ad.is_active ? 'bg-green-500/80' : 'bg-gray-500/60'
                    }`} style={{ color: '#fff' }}>
                      <div className={`w-1.5 h-1.5 rounded-full ${ad.is_active ? 'bg-white' : 'bg-gray-200'}`} />
                      {ad.is_active ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAdsMarketing;
